// Pure view helpers for the Bio module, ported verbatim from js/renderers-bio.js's
// humanizePositionTemplate / calcAge / formatDate + its inline HOPS/meta formatting.
// Kept pure (no DOM, no d3) so they're trivially unit-tested; the BioModule component
// just wires them into JSX. The `—` em-dash placeholders match the prototype exactly.

import type { ProtoPlayerBio } from "@catbento/shared";

const DASH = "—";

/** "center_forward" → "Center Forward". Empty/absent → "—" (renderers-bio.js:2). */
export function humanizePositionTemplate(tpl: string | null | undefined): string {
  if (!tpl) return DASH;
  return tpl
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Whole years from a DOB string, or null on an unparseable date (renderers-bio.js:7). */
export function calcAge(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

/**
 * "16 May 2002" (en-GB dd Mon yyyy). An unparseable date falls back to the raw
 * string, else "—" — exactly renderers-bio.js:17 (`dob || '—'`).
 */
export function formatDate(dob: string | null | undefined): string {
  if (!dob) return DASH;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return dob;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/** The Age meta row: "24 · 16 May 2002", or "—" when the DOB won't parse. */
export function formatAge(dob: string | null | undefined): string {
  const age = calcAge(dob);
  return age !== null ? `${age} · ${formatDate(dob)}` : DASH;
}

/** Height row: "175 cm" or "—". */
export function formatHeight(height: number | null | undefined): string {
  return height ? `${height} cm` : DASH;
}

/** Weight row: "68 kg" or "—". */
export function formatWeight(weight: number | null | undefined): string {
  return weight ? `${weight} kg` : DASH;
}

/**
 * Last-appearance row: "16 May 2002 · Arsenal WFC" (team appended only when
 * present), or "—" when there's no date (renderers-bio.js:89).
 */
export function formatLastAppearance(
  date: string | null | undefined,
  team: string | null | undefined,
): string {
  if (!date) return DASH;
  return `${formatDate(date)}${team ? ` · ${team}` : ""}`;
}

/** The numeric HOPS rating (0..1) as a display value: "66.9", or "—" on a data gap. */
export function formatHopsValue(hopsRating: ProtoPlayerBio["hopsRating"]): string {
  const rating = hopsRating && typeof hopsRating.rating === "number" ? hopsRating.rating : null;
  return rating !== null ? (rating * 100).toFixed(1) : DASH;
}

/**
 * Target width (%) for the HOPS rating bar's green fill. A present rating floors at
 * 2% so a tiny value still shows a sliver; a gap collapses to 0 (renderers-bio.js:102).
 */
export function hopsBarWidth(hopsRating: ProtoPlayerBio["hopsRating"]): number {
  const rating = hopsRating && typeof hopsRating.rating === "number" ? hopsRating.rating : null;
  return rating !== null ? Math.max(2, rating * 100) : 0;
}

/**
 * The legal-name line under the headline: the full name, shown only when it differs
 * from the short (display) name; otherwise a single space so the row still occupies
 * a line and the layout below stays fixed (renderers-bio.js:71).
 */
export function legalNameLine(shortName: string, name: string): string {
  return shortName !== name ? name : " ";
}
